import React from "react";
import { Check, Link as LinkIcon, Sparkles, Target, ExternalLink, Flame, Clock, Eye, Minus, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { fmtDate, relTime } from "@/lib/format";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

/* ------------------------------------------------------------------ */
/* Evidence label system - never present AI assumptions as fact       */
/* ------------------------------------------------------------------ */

export const EVIDENCE = {
  fact: { label: "Confirmed fact", Icon: Check, cls: "border-black/[0.08] bg-[hsl(var(--muted))]" },
  signal: { label: "External signal", Icon: LinkIcon, cls: "border-[hsl(210_60%_48%/0.22)] bg-[hsl(210_60%_48%/0.08)]" },
  inference: { label: "AI inference", Icon: Sparkles, cls: "border-[hsl(35_80%_48%/0.26)] bg-[hsl(35_80%_48%/0.10)]" },
  hypothesis: { label: "Commercial hypothesis", Icon: Target, cls: "border-[hsl(160_45%_38%/0.26)] bg-[hsl(160_45%_38%/0.10)]" },
};

export function EvidenceLabel({ kind = "fact", className, compact = false, testId }) {
  const m = EVIDENCE[kind] || EVIDENCE.fact;
  const Icon = m.Icon;
  return (
    <span data-testid={testId || "evidence-label-chip"} data-kind={kind} className={cn("inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border px-1.5 py-[2px] text-[9.5px] font-semibold uppercase tracking-[0.12em] text-foreground/80", m.cls, className)}>
      <Icon className="h-2.5 w-2.5" strokeWidth={2.4} />
      {!compact && m.label}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Confidence                                                           */
/* ------------------------------------------------------------------ */

const CONF = {
  high: { label: "High", dot: "bg-[hsl(var(--status-ok))]", chip: "border-[hsl(var(--status-ok)/0.28)] bg-[hsl(var(--status-ok)/0.10)]" },
  medium: { label: "Medium", dot: "bg-[hsl(var(--status-warn))]", chip: "border-[hsl(var(--status-warn)/0.30)] bg-[hsl(var(--status-warn)/0.10)]" },
  low: { label: "Low", dot: "bg-[hsl(var(--status-bad))]", chip: "border-[hsl(var(--status-bad)/0.28)] bg-[hsl(var(--status-bad)/0.10)]" },
};

export function ConfidenceChip({ level = "medium", className, testId, short = false }) {
  const m = CONF[level] || CONF.medium;
  return (
    <span data-testid={testId || `confidence-chip-${level}`} className={cn("inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-[3px] text-[11px] font-medium text-foreground/85", m.chip, className)}>
      <span className={cn("h-1.5 w-1.5 rounded-full", m.dot)} />
      {short ? m.label : `${m.label} confidence`}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Score                                                                */
/* ------------------------------------------------------------------ */

export function ScorePill({ score = 0, breakdown, size = "md", className, testId }) {
  const pill = (
    <span data-testid={testId || "opportunity-score-value"} className={cn("inline-flex items-center gap-2 rounded-full border border-black/[0.08] bg-white py-1 pl-3 pr-2.5 shadow-[0_1px_1px_hsl(30_12%_18%/0.03)]", size === "lg" && "py-1.5 pl-3.5 pr-3", className)}>
      <span className={cn("font-mono font-semibold tabular leading-none", size === "lg" ? "text-[18px]" : "text-[13px]")}>{score}</span>
      <span className={cn("relative h-1.5 overflow-hidden rounded-full bg-black/[0.07]", size === "lg" ? "w-16" : "w-10")} aria-hidden>
        <span className="absolute inset-y-0 left-0 rounded-full bg-[hsl(var(--ink))]" style={{ width: `${Math.max(0, Math.min(100, score))}%` }} />
      </span>
    </span>
  );
  if (!breakdown || !breakdown.length) return pill;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40" aria-label={`Opportunity score ${score} of 100`}>{pill}</button>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="end" className="max-w-[280px] p-3">
        <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Why this score</div>
        <div className="space-y-1">
          {breakdown.slice(0, 5).map((b) => (
            <div key={b.key} className="flex items-center justify-between gap-3 text-[12px]">
              <span className="truncate">{b.label}</span>
              <span className={cn("font-mono tabular", b.points < 0 && "text-[hsl(var(--status-bad))]")}>{b.points > 0 ? `+${b.points}` : b.points}</span>
            </div>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

export function ScoreBreakdown({ breakdown = [], score, className, testId }) {
  const pos = breakdown.filter((b) => b.points > 0);
  const neg = breakdown.filter((b) => b.points < 0);
  return (
    <div data-testid={testId || "score-breakdown"} className={cn("rounded-xl border border-black/[0.06] bg-[hsl(40_12%_98.5%)]", className)}>
      <div className="flex items-center justify-between border-b border-black/[0.06] px-4 py-2.5">
        <span className="text-[12px] font-semibold">Score breakdown</span>
        {typeof score === "number" ? <span className="font-mono text-[12px] tabular text-muted-foreground">{score} / 100</span> : null}
      </div>
      <div className="divide-y divide-black/[0.05] px-4">
        {[...pos, ...neg].map((b) => (
          <div key={b.key} className="flex items-start justify-between gap-3 py-2.5" data-testid="score-breakdown-row">
            <div className="min-w-0">
              <div className="text-[13px] font-medium leading-snug">{b.label}</div>
              {b.evidence ? <div className="mt-0.5 text-[12px] leading-snug text-muted-foreground">{b.evidence}</div> : null}
            </div>
            <span className={cn("inline-flex shrink-0 items-center gap-0.5 rounded-md border border-black/[0.08] bg-white px-2 py-0.5 font-mono text-[12px] tabular", b.points < 0 && "text-[hsl(var(--status-bad))]")}>
              {b.points < 0 ? <Minus className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
              {Math.abs(b.points)}
            </span>
          </div>
        ))}
        {!breakdown.length ? <div className="py-3 text-[12.5px] text-muted-foreground">No scoring factors matched.</div> : null}
      </div>
      <div className="border-t border-black/[0.06] px-4 py-2 text-[11px] text-muted-foreground">Deterministic scoring from configured rules. No black box.</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Timing / phase / stage                                               */
/* ------------------------------------------------------------------ */

export const PHASE_LABEL = { early_planning: "Early planning", planning: "Planning", specification: "Specification", procurement: "Procurement", execution: "Execution" };

export function phaseLabel(p) {
  return PHASE_LABEL[p] || (p || "").replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

const TIMING = {
  urgent: { label: "Act now", Icon: Flame, cls: "text-primary" },
  soon: { label: "Act soon", Icon: Clock, cls: "text-[hsl(var(--status-warn))]" },
  monitor: { label: "Monitor", Icon: Eye, cls: "text-muted-foreground" },
};

export function TimingChip({ timing = "monitor", phase, className, testId }) {
  const m = TIMING[timing] || TIMING.monitor;
  const Icon = m.Icon;
  return (
    <span data-testid={testId || `timing-chip-${timing}`} className={cn("inline-flex items-center gap-1.5 whitespace-nowrap text-[12px] font-medium", m.cls, className)}>
      <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />
      {m.label}
      {phase ? <span className="font-normal text-muted-foreground">· {phaseLabel(phase)}</span> : null}
    </span>
  );
}

const INTEL_STAGE_TOKEN = {
  signal: "new", identified: "new", contacted: "discovery", meeting: "discovery", qualified: "solution_design", solution: "solution_design",
  offer: "quote", negotiation: "negotiation", won: "won", lost: "lost",
};

export const INTEL_STAGES = [
  { key: "signal", label: "Signal" }, { key: "identified", label: "Account identified" }, { key: "contacted", label: "Contacted" }, { key: "meeting", label: "Meeting booked" },
  { key: "qualified", label: "Qualified" }, { key: "solution", label: "Solution design" }, { key: "offer", label: "Offer" }, { key: "negotiation", label: "Negotiation" },
  { key: "won", label: "Won" }, { key: "lost", label: "Lost" },
];

export function intelStageLabel(key, stages = INTEL_STAGES) {
  return stages.find((s) => s.key === key)?.label || (key || "").replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

export function IntelStageChip({ stage, stages, className, testId }) {
  return (
    <span data-testid={testId || `intel-stage-chip-${stage}`} className={cn("inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-black/[0.08] bg-white px-2.5 py-1 text-[11.5px] font-medium text-foreground/85", className)}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: `hsl(var(--stage-${INTEL_STAGE_TOKEN[stage] || "new"}))` }} />
      {intelStageLabel(stage, stages)}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Source line                                                          */
/* ------------------------------------------------------------------ */

export function SourceLine({ signal, className, showDate = true }) {
  if (!signal) return null;
  const url = signal.source_url;
  const domain = url ? String(url).replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0] : null;
  return (
    <span className={cn("inline-flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[12px] text-muted-foreground", className)}>
      <span className="text-muted-foreground/80">Source:</span>
      {url ? (
        <a href={url} target="_blank" rel="noreferrer" data-testid="evidence-source-link" className="inline-flex items-center gap-1 font-medium text-foreground/80 underline-offset-2 hover:text-primary hover:underline">
          {domain} <ExternalLink className="h-3 w-3" />
        </a>
      ) : (
        <span data-testid="evidence-source-link" className="font-medium text-foreground/80">{signal.source || "Unknown"}</span>
      )}
      {showDate && signal.detected_date ? (
        <>
          <span className="text-border">·</span>
          <span className="font-mono tabular" title={fmtDate(signal.detected_date, { year: true })}>{fmtDate(signal.detected_date)}</span>
          <span className="text-muted-foreground/70">({relTime(signal.detected_date)})</span>
        </>
      ) : null}
      {signal.simulated ? <span className="rounded border border-dashed border-black/[0.14] px-1 text-[9.5px] uppercase tracking-wider text-muted-foreground/70" title="Demo data - simulated source">demo</span> : null}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Misc                                                                 */
/* ------------------------------------------------------------------ */

export function ValueRange({ label, className, testId }) {
  const missing = !label || label === "Missing data";
  return (
    <span data-testid={testId} className={cn("font-mono tabular", missing ? "text-muted-foreground" : "font-medium", className)}>{missing ? "Missing data" : label}</span>
  );
}

export function SectionLabel({ children, right, className }) {
  return (
    <div className={cn("mb-2 flex items-center justify-between gap-3", className)}>
      <span className="eyebrow text-[10.5px]">{children}</span>
      {right}
    </div>
  );
}

export function Chip({ children, className, testId }) {
  return <span data-testid={testId} className={cn("inline-flex items-center whitespace-nowrap rounded-full border border-black/[0.08] bg-white px-2.5 py-1 text-[12px] text-foreground/85", className)}>{children}</span>;
}

export const LANGS = [
  { key: "no", label: "Norwegian" },
  { key: "sv", label: "Swedish" },
  { key: "da", label: "Danish" },
  { key: "en", label: "English" },
];

export function langLabel(k) {
  return LANGS.find((l) => l.key === k)?.label || "English";
}

export function kindLabel(kind) {
  return kind === "expansion" ? "Expansion" : "New business";
}
